import {
  Component,
  signal,
  computed,
  ChangeDetectionStrategy,
  inject,
  OnInit,
  OnDestroy,
  DestroyRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';

import { TabsModule } from 'primeng/tabs';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { MessageModule } from 'primeng/message';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { PushSettingsV2Api, PushDeviceEntry } from '@tourfold/libraries/tf-api-client-frontend-v2';
import {
  TfDataTableComponent,
  TfColumnDefDirective,
  ColumnDef,
  TfPageEvent,
  SettingsListHeader,
} from '@tourfold/libraries/shared-ui';
import { from, Subject, Subscription, finalize } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import {
  readTabFromQueryParams,
  syncTabToUrl,
  readStringParam,
  readNumberParam,
  syncQueryParams,
  nonEmptyOrUndefined,
} from '@tourfold/libraries/shared-util';

import { SseV2Service, PushTestConfirmedEventDto } from '@tourfold/shared-kernels/realtime';
import {
  PushDeviceFilters,
  PushDeviceFilterValues,
  DevicePlatformValue,
} from './shared/push-device-filters/push-device-filters';
import { TestPushDialog } from './shared/test-push-dialog/test-push-dialog';
import { TestLogEntry } from './shared/test-push-dialog/test-log-entry';
import { providePushNotificationsAiPageContext } from './settings-push-notifications.ai-page-context';
import {
  resolveSettingsLoadErrorDetails,
  resolveSettingsProblemMessage,
  SettingsInlineMessage,
  SettingsLoadError,
  type SettingsLoadErrorDetails,
} from '@tourfold/features/settings-ui';

const TAB_VALUES = ['overview', 'connection-test'] as const;

// Maps the problem types the test-push endpoint can return to actionable log messages.
// A stale device token surfaces as a conflict (the token was removed server-side); a
// missing push configuration surfaces as a business-rule violation. Anything else falls
// back to the generic send-failed message.
const PUSH_TEST_SEND_PROBLEM_KEYS: Readonly<Record<string, string>> = {
  'resource-conflict': 'SETTINGS.PUSH_NOTIFICATIONS.TEST_LOG.SEND_FAILED_UNREGISTERED',
  'business-rule-violation': 'SETTINGS.PUSH_NOTIFICATIONS.TEST_LOG.SEND_FAILED_NO_CONFIG',
};
const VALID_PLATFORMS: readonly DevicePlatformValue[] = ['IOS', 'ANDROID', 'UNKNOWN'];
const TEST_TIMEOUT_MS = 30_000;
const SEARCH_DEBOUNCE_MS = 300;
const DEFAULT_PAGE_SIZE = 25;

function isValidPlatform(value: string): value is DevicePlatformValue {
  return (VALID_PLATFORMS as readonly string[]).includes(value);
}

@Component({
  selector: 'tf-settings-page-push-notifications',
  imports: [
    SettingsListHeader,
    TabsModule,
    ButtonModule,
    TooltipModule,
    MessageModule,
    ProgressSpinnerModule,
    TranslateModule,
    TfDataTableComponent,
    TfColumnDefDirective,
    PushDeviceFilters,
    TestPushDialog,
    SettingsInlineMessage,
    SettingsLoadError,
  ],
  templateUrl: './settings-push-notifications.html',
  styleUrl: './settings-push-notifications.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PushNotificationsSettings implements OnInit, OnDestroy {
  private readonly pushSettingsService = inject(PushSettingsV2Api);
  private readonly translate = inject(TranslateService);
  private readonly sseService = inject(SseV2Service);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  readonly activeTab = signal(
    readTabFromQueryParams(this.route.snapshot.queryParams, 'tab', TAB_VALUES, 'overview')
  );

  readonly isOverviewLoading = signal(false);
  readonly overviewHasLoadedOnce = signal(false);
  readonly overviewInitialLoadFailed = signal(false);
  readonly overviewError = signal<string | null>(null);
  readonly overviewErrorDetails = signal<SettingsLoadErrorDetails | null>(null);
  readonly totalDevices = signal(0);
  readonly totalUsersWithDevices = signal(0);
  readonly hasCustomConfig = signal(false);

  readonly devices = signal<PushDeviceEntry[]>([]);
  readonly isDevicesLoading = signal(false);
  readonly devicesHasLoadedOnce = signal(false);
  readonly devicesInitialLoadFailed = signal(false);
  readonly devicesError = signal<string | null>(null);
  readonly devicesErrorDetails = signal<SettingsLoadErrorDetails | null>(null);
  readonly totalRecords = signal(0);
  readonly searchQuery = signal('');
  readonly platformTypeFilter = signal<DevicePlatformValue | null>(null);
  readonly currentPage = signal(0);
  readonly pageSize = signal(DEFAULT_PAGE_SIZE);

  readonly hasActiveFilters = computed(() => this.platformTypeFilter() !== null);

  readonly isTestDialogVisible = signal(false);
  readonly testingDevice = signal<PushDeviceEntry | null>(null);
  readonly testLogEntries = signal<TestLogEntry[]>([]);
  readonly testInProgress = signal(false);

  private pendingTestId: string | null = null;
  private bufferedTestEvents: PushTestConfirmedEventDto[] = [];
  private testTimeout: ReturnType<typeof setTimeout> | null = null;

  private readonly searchSubject = new Subject<string>();
  private sseSubscription: Subscription | null = null;

  readonly columns: ColumnDef[] = [
    { field: 'user', title: 'SETTINGS.PUSH_NOTIFICATIONS.USER', type: 'custom', width: '24%' },
    {
      field: 'platformType',
      title: 'SETTINGS.PUSH_NOTIFICATIONS.PLATFORM',
      type: 'custom',
      width: '14%',
    },
    { field: 'deviceInfo', title: 'SETTINGS.PUSH_NOTIFICATIONS.DEVICE_INFO', type: 'text' },
    {
      field: 'tokenPrefix',
      title: 'SETTINGS.PUSH_NOTIFICATIONS.TOKEN_PREFIX',
      type: 'custom',
      width: '14%',
    },
    {
      field: 'createdAt',
      title: 'SETTINGS.PUSH_NOTIFICATIONS.REGISTERED_AT',
      type: 'date',
      dateFormat: 'short',
      width: '14%',
    },
    {
      field: '_actions',
      title: 'SETTINGS.PUSH_NOTIFICATIONS.ACTIONS',
      type: 'custom',
      width: '8%',
    },
  ];

  constructor() {
    this.hydrateFromQueryParams();
    this.setupSearchDebounce();
    providePushNotificationsAiPageContext({
      activeTab: this.activeTab,
      devices: this.devices,
      searchQuery: this.searchQuery,
      platformTypeFilter: this.platformTypeFilter,
      testingDevice: this.testingDevice,
    });
  }

  ngOnInit(): void {
    this.loadOverview();
    this.subscribeToPushTestEvents();
    if (this.activeTab() === 'connection-test') {
      this.loadDevices();
    }
  }

  ngOnDestroy(): void {
    this.sseSubscription?.unsubscribe();
    this.clearTestTimeout();
  }

  onTabChange(tab: string): void {
    this.activeTab.set(tab);
    syncTabToUrl(this.router, this.route, 'tab', tab);
    if (tab === 'connection-test') {
      this.loadDevices();
    } else if (tab === 'overview') {
      this.loadOverview();
    }
  }

  onSearchChange(query: string): void {
    this.searchSubject.next(query);
  }

  onFilterApplied(values: PushDeviceFilterValues): void {
    this.platformTypeFilter.set(values.platformType);
    this.currentPage.set(0);
    this.syncFiltersToUrl();
    this.loadDevices();
  }

  onFilterCleared(): void {
    this.platformTypeFilter.set(null);
    this.currentPage.set(0);
    this.syncFiltersToUrl();
    this.loadDevices();
  }

  onPageChange(event: TfPageEvent): void {
    const page = Math.floor(event.first / event.rows);
    this.currentPage.set(page);
    this.pageSize.set(event.rows);
    this.syncFiltersToUrl();
    this.loadDevices();
  }

  reloadDevices(): void {
    this.loadDevices();
  }

  reloadOverview(): void {
    this.loadOverview();
  }

  getPlatformIcon(platform: string | undefined): string {
    if (platform === 'IOS') return 'pi pi-apple';
    if (platform === 'ANDROID') return 'pi pi-android';
    return 'pi pi-mobile';
  }

  getPlatformLabelKey(platform: string | undefined): string {
    if (platform === 'IOS') return 'SETTINGS.PUSH_NOTIFICATIONS.PLATFORM_IOS';
    if (platform === 'ANDROID') return 'SETTINGS.PUSH_NOTIFICATIONS.PLATFORM_ANDROID';
    return 'SETTINGS.PUSH_NOTIFICATIONS.PLATFORM_UNKNOWN';
  }

  openTestDialog(device: PushDeviceEntry): void {
    this.testingDevice.set(device);
    this.testLogEntries.set([]);
    this.testInProgress.set(false);
    this.pendingTestId = null;
    this.bufferedTestEvents = [];
    this.clearTestTimeout();
    this.isTestDialogVisible.set(true);
  }

  onTestDialogVisibleChange(visible: boolean): void {
    this.isTestDialogVisible.set(visible);
    if (!visible) {
      this.testingDevice.set(null);
      this.testLogEntries.set([]);
      this.testInProgress.set(false);
      this.pendingTestId = null;
      this.bufferedTestEvents = [];
      this.clearTestTimeout();
    }
  }

  onTestDialogSend(): void {
    const device = this.testingDevice();
    if (!device?.id || this.testInProgress()) {
      return;
    }

    this.testInProgress.set(true);
    this.testLogEntries.set([
      this.entry('SETTINGS.PUSH_NOTIFICATIONS.TEST_LOG.SCHEDULING', 'info'),
    ]);

    from(this.pushSettingsService.sendPushTest({ deviceTokenId: device.id })).subscribe({
      next: (response) => {
        const testId = response.testId ?? '';
        this.pendingTestId = testId;
        this.appendLog(this.entry('SETTINGS.PUSH_NOTIFICATIONS.TEST_LOG.SCHEDULED', 'info'));
        this.appendLog(this.entry('SETTINGS.PUSH_NOTIFICATIONS.TEST_LOG.WAITING', 'info'));

        const matching = this.bufferedTestEvents.filter((e) => e.test_id === testId);
        this.bufferedTestEvents = [];
        matching.forEach((e) => this.handleConfirmation(e));

        if (this.testInProgress() && this.pendingTestId === testId) {
          this.testTimeout = setTimeout(() => {
            if (this.pendingTestId === testId && this.testInProgress()) {
              this.appendLog(this.entry('SETTINGS.PUSH_NOTIFICATIONS.TEST_LOG.TIMED_OUT', 'error'));
              this.testInProgress.set(false);
              this.pendingTestId = null;
            }
          }, TEST_TIMEOUT_MS);
        }
      },
      error: (error: unknown) => {
        this.bufferedTestEvents = [];
        this.pendingTestId = null;
        this.testInProgress.set(false);
        void this.logSendError(error);
      },
    });
  }

  private async logSendError(error: unknown): Promise<void> {
    const message = await resolveSettingsProblemMessage(error, this.translate, {
      fallbackKey: 'SETTINGS.PUSH_NOTIFICATIONS.TEST_LOG.SEND_FAILED',
      problemTranslationKeys: PUSH_TEST_SEND_PROBLEM_KEYS,
    });
    this.appendLog({ message: message.text, severity: 'error' });
  }

  private hydrateFromQueryParams(): void {
    const params = this.route.snapshot.queryParams;
    this.searchQuery.set(readStringParam(params, 'search', ''));
    this.currentPage.set(readNumberParam(params, 'page', 0));
    this.pageSize.set(readNumberParam(params, 'size', DEFAULT_PAGE_SIZE));

    const platform = nonEmptyOrUndefined(readStringParam(params, 'platform_type', ''));
    this.platformTypeFilter.set(platform && isValidPlatform(platform) ? platform : null);
  }

  private setupSearchDebounce(): void {
    this.searchSubject
      .pipe(
        debounceTime(SEARCH_DEBOUNCE_MS),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((query) => {
        this.searchQuery.set(query);
        this.currentPage.set(0);
        this.syncFiltersToUrl();
        this.loadDevices();
      });
  }

  private loadOverview(): void {
    const hadLoadedOnce = this.overviewHasLoadedOnce();
    this.isOverviewLoading.set(true);
    from(this.pushSettingsService.getPushNotificationOverview())
      .pipe(finalize(() => this.isOverviewLoading.set(false)))
      .subscribe({
        next: (data) => {
          this.totalDevices.set(data.totalDevices ?? 0);
          this.totalUsersWithDevices.set(data.totalUsersWithDevices ?? 0);
          this.hasCustomConfig.set(data.hasCustomConfig ?? false);
          this.overviewError.set(null);
          this.overviewErrorDetails.set(null);
          this.overviewInitialLoadFailed.set(false);
          this.overviewHasLoadedOnce.set(true);
        },
        error: (error: unknown) => {
          void this.handleOverviewLoadError(error, hadLoadedOnce);
        },
      });
  }

  private loadDevices(): void {
    const hadLoadedOnce = this.devicesHasLoadedOnce();
    this.isDevicesLoading.set(true);

    const search = nonEmptyOrUndefined(this.searchQuery().trim());
    const platformType = this.platformTypeFilter() ?? undefined;

    from(
      this.pushSettingsService.listPushDevices({
        page: this.currentPage(),
        size: this.pageSize(),
        search,
        platformType,
      })
    )
      .pipe(finalize(() => this.isDevicesLoading.set(false)))
      .subscribe({
        next: (response) => {
          this.devices.set(response.items ?? []);
          this.totalRecords.set(response.page?.totalElements ?? 0);
          this.devicesError.set(null);
          this.devicesErrorDetails.set(null);
          this.devicesInitialLoadFailed.set(false);
          this.devicesHasLoadedOnce.set(true);
        },
        error: (error: unknown) => {
          void this.handleDevicesLoadError(error, hadLoadedOnce);
        },
      });
  }

  clearOverviewError(): void {
    this.overviewError.set(null);
  }

  clearDevicesError(): void {
    this.devicesError.set(null);
  }

  private async handleOverviewLoadError(error: unknown, hadLoadedOnce: boolean): Promise<void> {
    this.overviewError.set(this.translate.instant('SETTINGS.PUSH_NOTIFICATIONS.LOAD_ERROR'));
    this.overviewErrorDetails.set(await resolveSettingsLoadErrorDetails(error));
    this.overviewInitialLoadFailed.set(!hadLoadedOnce);
    this.overviewHasLoadedOnce.set(true);
  }

  private async handleDevicesLoadError(error: unknown, hadLoadedOnce: boolean): Promise<void> {
    this.devicesError.set(this.translate.instant('SETTINGS.PUSH_NOTIFICATIONS.LOAD_ERROR'));
    this.devicesErrorDetails.set(await resolveSettingsLoadErrorDetails(error));
    this.devicesInitialLoadFailed.set(!hadLoadedOnce);
    this.devicesHasLoadedOnce.set(true);
  }

  private subscribeToPushTestEvents(): void {
    this.sseSubscription = this.sseService
      .on('PUSH_TEST_CONFIRMED:V2')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => {
        const pushEvent = event as PushTestConfirmedEventDto;
        if (this.pendingTestId && pushEvent.test_id === this.pendingTestId) {
          this.handleConfirmation(pushEvent);
        } else if (!this.pendingTestId) {
          this.bufferedTestEvents.push(pushEvent);
        }
      });
  }

  private handleConfirmation(event: PushTestConfirmedEventDto): void {
    if (this.pendingTestId !== event.test_id) {
      return;
    }
    this.appendLog(this.entry('SETTINGS.PUSH_NOTIFICATIONS.TEST_LOG.CONFIRMED', 'success'));
    this.testInProgress.set(false);
    this.pendingTestId = null;
    this.clearTestTimeout();
  }

  private appendLog(entry: TestLogEntry): void {
    this.testLogEntries.update((entries) => [...entries, entry]);
  }

  private entry(key: string, severity: TestLogEntry['severity']): TestLogEntry {
    return { message: this.translate.instant(key), severity };
  }

  private clearTestTimeout(): void {
    if (this.testTimeout) {
      clearTimeout(this.testTimeout);
      this.testTimeout = null;
    }
  }

  private syncFiltersToUrl(): void {
    const search = nonEmptyOrUndefined(this.searchQuery().trim());
    syncQueryParams(this.router, this.route, {
      search,
      platform_type: this.platformTypeFilter() ?? undefined,
      page: this.currentPage() === 0 ? undefined : String(this.currentPage()),
      size: this.pageSize() === DEFAULT_PAGE_SIZE ? undefined : String(this.pageSize()),
    });
  }
}
